import { WeatherLocationFavouriteItem } from '@deckee/api-app-main';
import { getLayerDefinitions } from '../../../../../services/map-layer';

export type WeatherLocationFavouriteItemType =
  | 'marker'
  | 'googlePlace'
  | 'coordinates';

export interface FavouriteSearchIdentity {
  itemType: WeatherLocationFavouriteItemType;
  itemId: string;
}

export interface FavouriteLocationFields {
  markerId?: string | null;
  placeId?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  lat?: number;
  lon?: number;
}

export const formatCoordinatesItemId = (
  latitude: number,
  longitude: number,
): string => `${latitude.toFixed(6)},${longitude.toFixed(6)}`;

export const parseCoordinate = (
  value: number | string | null | undefined,
): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getFavouriteIdentityFromLocation = (
  location: FavouriteLocationFields,
): FavouriteSearchIdentity | null => {
  if (location.markerId) {
    return { itemType: 'marker', itemId: location.markerId };
  }

  if (location.placeId) {
    return { itemType: 'googlePlace', itemId: location.placeId };
  }

  const latitude =
    location.lat ?? parseCoordinate(location.latitude ?? undefined);
  const longitude =
    location.lon ?? parseCoordinate(location.longitude ?? undefined);

  if (latitude == null || longitude == null) {
    return null;
  }

  return {
    itemType: 'coordinates',
    itemId: formatCoordinatesItemId(latitude, longitude),
  };
};

export const findFavouriteForLocation = (
  favourites: WeatherLocationFavouriteItem[],
  location: FavouriteLocationFields,
): WeatherLocationFavouriteItem | undefined => {
  const identity = getFavouriteIdentityFromLocation(location);

  if (!identity) {
    return undefined;
  }

  return favourites.find(
    (favourite) =>
      favourite.itemType === identity.itemType &&
      favourite.itemId === identity.itemId,
  );
};

export const getMarkerDisplayName = (
  markerType?: string | null,
): string | undefined => {
  if (!markerType) {
    return undefined;
  }

  if (markerType === 'beachEmergencyNumberMarker') {
    return 'Beach emergency number';
  }

  return getLayerDefinitions()[markerType]?.displayName;
};

const getRegionStateDescription = (
  region?: string | null,
  state?: string | null,
): string | undefined => {
  const joined = [region, state].filter(Boolean).join(', ');
  return joined || undefined;
};

export interface SearchResultDisplayFields {
  title?: string;
  name?: string;
  placeDescription?: string;
  state?: string;
  region?: string;
  markerType?: string;
}

/** Secondary line for weather search result rows (not favourites list). */
export const getSearchItemSecondaryDescription = (
  item: Pick<
    SearchResultDisplayFields,
    'placeDescription' | 'state' | 'region' | 'markerType'
  >,
): string | undefined =>
  item.placeDescription ||
  item.state ||
  item.region ||
  getMarkerDisplayName(item.markerType) ||
  undefined;

export const getSearchResultDisplay = (
  item: SearchResultDisplayFields,
): { displayName: string; descriptionText?: string } => ({
  displayName: item.title || item.name || 'Unknown',
  descriptionText: getSearchItemSecondaryDescription(item),
});

export const getFavouriteDisplayDescription = (
  favourite: Pick<
    WeatherLocationFavouriteItem,
    'locationDescription' | 'state' | 'region' | 'markerType'
  >,
): string | undefined =>
  favourite.locationDescription ||
  getRegionStateDescription(favourite.region, favourite.state) ||
  getMarkerDisplayName(favourite.markerType) ||
  undefined;

export const favouriteToSearchItem = (
  favourite: WeatherLocationFavouriteItem,
) => ({
  title: favourite.name,
  name: favourite.name,
  latitude: favourite.latitude,
  longitude: favourite.longitude,
  lat: favourite.latitude,
  lon: favourite.longitude,
  state: favourite.state || undefined,
  region: favourite.region || undefined,
  placeDescription: favourite.locationDescription || undefined,
  markerType: favourite.markerType || undefined,
  markerId: favourite.markerId || undefined,
  placeId: favourite.placeId || undefined,
  favouriteId: String(favourite.id),
});

export const buildAddWeatherLocationFavouritePayload = ({
  name,
  latitude,
  longitude,
  state,
  region,
  locationDescription,
  markerType,
  markerId,
  placeId,
}: {
  name: string;
  latitude: number;
  longitude: number;
  state?: string | null;
  region?: string | null;
  locationDescription?: string | null;
  markerType?: string | null;
  markerId?: string | null;
  placeId?: string | null;
}) => {
  const identity = getFavouriteIdentityFromLocation({
    markerId,
    placeId,
    latitude,
    longitude,
  });

  if (!identity) {
    throw new Error('Unable to determine favourite identity for location');
  }

  const resolvedLocationDescription =
    locationDescription ||
    getRegionStateDescription(region, state) ||
    getMarkerDisplayName(markerType) ||
    undefined;

  return {
    name,
    latitude,
    longitude,
    state: state || undefined,
    region: region || undefined,
    locationDescription: resolvedLocationDescription,
    markerType: markerType || undefined,
    markerId: markerId || undefined,
    placeId: placeId || undefined,
    itemType: identity.itemType,
    itemId: identity.itemId,
  };
};
